import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Swal from "sweetalert2";
import { apiRequest, getImageUrl } from "../../../core/common/api";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import AppImage from "../../../shared/AppImage";

type Category = {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  image?: string;
  isActive?: boolean;
  featured?: boolean;
};

type CategoryForm = {
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
  featured: boolean;
};

type SubCategoryForm = CategoryForm & {
  category: string;
};

const categoryInitial: CategoryForm = {
  name: "",
  slug: "",
  description: "",
  isActive: true,
  featured: true,
};

const subCategoryInitial: SubCategoryForm = {
  name: "",
  slug: "",
  description: "",
  isActive: true,
  featured: true,
  category: "",
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const CategoriesAdd = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editState = location.state as {
    editType?: "category" | "subcategory";
    item?: any;
  } | null;
  const editType = editState?.editType;
  const editItem = editState?.item;
  const isEditMode = Boolean(editType && editItem?._id);

  const [tab, setTab] = useState<"category" | "subcategory">("category");

  const [categories, setCategories] = useState<Category[]>([]);
  const [category, setCategory] = useState<CategoryForm>(categoryInitial);

  const [subCategory, setSubCategory] =
    useState<SubCategoryForm>(subCategoryInitial);

  const [categoryImage, setCategoryImage] = useState<File | null>(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState("");

  const [subCategoryImage, setSubCategoryImage] = useState<File | null>(null);
  const [subCategoryImagePreview, setSubCategoryImagePreview] = useState("");

  const [saving, setSaving] = useState(false);

  const loadCategories = async () => {
    try {
      const result = await apiRequest("/categories", "GET");
      setCategories(result.categories || []);
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (!isEditMode) return;

    if (editType === "category") {
      setTab("category");
      setCategory({
        name: editItem.name || "",
        slug: editItem.slug || slugify(editItem.name || ""),
        description: editItem.description || "",
        isActive: editItem.isActive !== false,
        featured: editItem.featured !== false,
      });
      setCategoryImagePreview(editItem.image || "");
      return;
    }

    setTab("subcategory");
    setSubCategory({
      category:
        editItem.parent?._id ||
        editItem.category?._id ||
        editItem.category ||
        "",
      name: editItem.name || "",
      slug: editItem.slug || slugify(editItem.name || ""),
      description: editItem.description || "",
      isActive: editItem.isActive !== false,
      featured: editItem.featured !== false,
    });
    setSubCategoryImagePreview(editItem.image || "");
  }, [editItem, editType, isEditMode]);

  const resetForm = () => {
    setCategory(categoryInitial);
    setSubCategory(subCategoryInitial);
    setCategoryImage(null);
    setSubCategoryImage(null);
    setCategoryImagePreview("");
    setSubCategoryImagePreview("");
  };

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isCategory = tab === "category";
    const data = isCategory ? category : subCategory;
    const image = isCategory ? categoryImage : subCategoryImage;

    if (!data.name.trim()) {
      return Swal.fire("Error", "Name is required", "error");
    }

    if (!isCategory && !subCategory.category) {
      return Swal.fire("Error", "Parent category is required", "error");
    }

    setSaving(true);

    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      if (image) {
        formData.append("image", image);
      }

      const endpoint = isCategory
        ? `/categories${isEditMode ? `/${editItem._id}` : ""}`
        : `/sub-categories${isEditMode ? `/${editItem._id}` : ""}`;

      const result = await apiRequest(
        endpoint,
        isEditMode ? "PUT" : "POST",
        formData,
      );

      if (!result.success) {
        throw new Error(result.message || "Unable to save");
      }

      if (isCategory) {
        await loadCategories();
      }

      if (!isEditMode) {
        resetForm();
      }

      Swal.fire({
        title: isEditMode
          ? isCategory
            ? "Category Updated"
            : "Subcategory Updated"
          : isCategory
            ? "Category Added"
            : "Subcategory Added",
        text: result.message,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      if (isEditMode) {
        navigate("/categories");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MANAGEMENT</div>

          <h1 className="page-title">
            {isEditMode ? "Edit" : "Add"} <em>Categories</em>
          </h1>

          <div className="page-sub">Create categories and subcategories.</div>
        </div>

        <div className="page-hdr-actions">
          <button
            type="button"
            disabled={isEditMode}
            className={`btn-sm ${tab === "category" ? "btn-primary" : ""}`}
            onClick={() => setTab("category")}
          >
            🗂️ Add Category
          </button>

          {/* <button
            type="button"
            disabled={isEditMode}
            className={`btn-sm ${tab === "subcategory" ? "btn-primary" : ""}`}
            onClick={() => setTab("subcategory")}
          >
            🔗 Add Subcategory
          </button> */}
        </div>
      </div>

      <form className="card category-form-card" onSubmit={submitForm}>
        <div className="card-header">
          <div>
            <h2>{tab === "category" ? "Add Category" : "Add Subcategory"}</h2>

            <p>
              {tab === "category"
                ? isEditMode
                  ? "Update product category"
                  : "Create a new product category"
                : isEditMode
                  ? "Update subcategory details"
                  : "Create a subcategory under a category"}
            </p>
          </div>
        </div>

        <div className="card-body form-grid">
          <div className="form-left">
            {tab === "subcategory" && (
              <Field label="Parent Category" required>
                <select
                  required
                  value={subCategory.category}
                  onChange={(event) =>
                    setSubCategory({
                      ...subCategory,
                      category: event.target.value,
                    })
                  }
                >
                  <option value="">Select category</option>

                  {categories.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field
              label={tab === "category" ? "Category Name" : "Subcategory Name"}
              required
            >
              <input
                required
                maxLength={100}
                value={tab === "category" ? category.name : subCategory.name}
                placeholder="Enter name"
                onChange={(event) => {
                  const name = event.target.value;

                  if (tab === "category") {
                    setCategory({
                      ...category,
                      name,
                      slug: slugify(name),
                    });
                  } else {
                    setSubCategory({
                      ...subCategory,
                      name,
                      slug: slugify(name),
                    });
                  }
                }}
              />
            </Field>

            <Field label="Slug" required>
              <input
                required
                value={tab === "category" ? category.slug : subCategory.slug}
                placeholder="category-slug"
                onChange={(event) => {
                  const slug = slugify(event.target.value);

                  if (tab === "category") {
                    setCategory({ ...category, slug });
                  } else {
                    setSubCategory({
                      ...subCategory,
                      slug,
                    });
                  }
                }}
              />
            </Field>

            <Field label="Description">
              <textarea
                rows={5}
                maxLength={1000}
                value={
                  tab === "category"
                    ? category.description
                    : subCategory.description
                }
                placeholder="Write description..."
                onChange={(event) => {
                  const description = event.target.value;

                  if (tab === "category") {
                    setCategory({
                      ...category,
                      description,
                    });
                  } else {
                    setSubCategory({
                      ...subCategory,
                      description,
                    });
                  }
                }}
              />
            </Field>
          </div>

          <div className="form-right">
            <ImageUpload
              label={
                tab === "category" ? "Category Image" : "Subcategory Image"
              }
              file={tab === "category" ? categoryImage : subCategoryImage}
              previewUrl={
                tab === "category"
                  ? categoryImagePreview
                  : subCategoryImagePreview
              }
              onChange={
                tab === "category"
                  ? (file) => {
                      setCategoryImage(file);
                      if (file) setCategoryImagePreview("");
                    }
                  : (file) => {
                      setSubCategoryImage(file);
                      if (file) setSubCategoryImagePreview("");
                    }
              }
            />

            <ToggleSwitch
              label="Active"
              checked={
                tab === "category" ? category.isActive : subCategory.isActive
              }
              onChange={(isActive) => {
                if (tab === "category") {
                  setCategory({ ...category, isActive });
                } else {
                  setSubCategory({
                    ...subCategory,
                    isActive,
                  });
                }
              }}
              checkedLabel="Active"
              uncheckedLabel="Inactive"
            />

            <ToggleSwitch
              label="Featured"
              checked={
                tab === "category" ? category.featured : subCategory.featured
              }
              onChange={(featured) => {
                if (tab === "category") {
                  setCategory({ ...category, featured });
                } else {
                  setSubCategory({
                    ...subCategory,
                    featured,
                  });
                }
              }}
              checkedLabel="Featured"
              uncheckedLabel="Not featured"
            />
          </div>
        </div>

        <div className="card-footer">
          <button
            type="button"
            className="btns btn-light"
            onClick={resetForm}
            disabled={saving}
          >
            ✖ Clear
          </button>

          <button type="submit" className="btns btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </>
  );
};

const Field = ({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <label className="form-field">
    <span>
      {label} {required && <b>*</b>}
    </span>

    {children}
  </label>
);

const ImageUpload = ({
  label,
  file,
  previewUrl,
  onChange,
}: {
  label: string;
  file: File | null;
  previewUrl?: string;
  onChange: (file: File | null) => void;
}) => {
  const selectImage = (event: ChangeEvent<HTMLInputElement>) => {
    const image = event.target.files?.[0];

    if (!image) return;

    if (image.size > 5 * 1024 * 1024) {
      event.target.value = "";

      return Swal.fire("Error", "Image must be less than 5MB", "error");
    }

    onChange(image);
  };

  return (
    <label className="image-field">
      <span>{label}</span>

      <div className="image-upload">
        {file || previewUrl ? (
          <AppImage
            src={file ? URL.createObjectURL(file) : getImageUrl(previewUrl)}
            alt={label}
            crossOrigin={file ? undefined : "anonymous"}
          />
        ) : (
          <div className="upload-placeholder">
            <strong>☁️</strong>
            <b>Upload image</b>
            <small>PNG, JPG or WEBP up to 5MB</small>
          </div>
        )}

        <input
          hidden
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={selectImage}
        />
      </div>
    </label>
  );
};

export default CategoriesAdd;
